// timeFormat.js — THE clock formatter. Every time a user READS is built here.
//
// Owner brief 2026-10-27: the whole product shows 12-hour clocks with AM/PM
// ("h:mm AM/PM" in English, "h:mm ص/م" in Arabic). Storage, API payloads, Cairo
// day-bucket keys, attendance math and every computation stay 24h ISO — only
// what the user reads changes, and it changes in ONE place so it cannot drift
// site by site. verify-time-12h.js runs the fixtures below and fails the build
// on any ad-hoc 24h render outside this module.
//
// Two kinds of input, two entry points — never cross them:
//   - an INSTANT (Date / ISO string / ms): fmtTime12 / fmtDateTime12 resolve the
//     Cairo wall clock through Intl by zone NAME (verify-cairo-time.js doctrine:
//     no offset arithmetic, no getHours(), no setHours()).
//   - a NAIVE WALL-CLOCK STRING ("YYYY-MM-DDTHH:mm" — the shape callbackTime is
//     stored in, already Cairo wall-clock by the storage contract): fmtWallTime12
//     reads the digits. Running it through new Date() would re-interpret it in
//     the viewer's zone, which is the bug the callback strip was written around.
//
// Midnight is 12:00 AM, noon is 12:00 PM. Invalid input renders "" so a caller
// can keep its own empty-answer wording ("—", "-", "").
//
// The backend twin is crm-aro-backend/time-format.js — duplicated, not imported,
// because there is no shared-module path (Railway deploys only /crm-aro-backend;
// CRA refuses imports from outside src/). The gate asserts the two agree.

var CAIRO_TZ = "Africa/Cairo";
var MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// One Intl instance per process — toLocaleTimeString builds a formatter per call
// and the dashboard measured that at ~10s over 1,000 rows (see _dashboardExactTime).
var NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
var _cairoParts = null;
var cairoClockParts = function (d) {
  if (d == null || d === "") return null;   // new Date(null) is the epoch, not "nothing"
  // The naive wire shape ("YYYY-MM-DDTHH:mm", no seconds, no zone) IS the Cairo
  // wall clock already — read the digits instead of letting new Date() decide.
  var nv = typeof d === "string" ? NAIVE_RE.exec(d.trim()) : null;
  if (nv) return { y: Number(nv[1]), m: Number(nv[2]), d: Number(nv[3]), h: Number(nv[4]), mi: Number(nv[5]), s: 0 };
  var dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  if (!_cairoParts) {
    _cairoParts = new Intl.DateTimeFormat("en-US", {
      timeZone: CAIRO_TZ, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
  }
  var p = {};
  _cairoParts.formatToParts(dt).forEach(function (x) { p[x.type] = x.value; });
  // Some Intl builds print "24" for midnight under hour12:false — normalise.
  var h = p.hour === "24" ? 0 : Number(p.hour);
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h: h, mi: Number(p.minute), s: Number(p.second) || 0 };
};

// AM/PM words. `t` is the page's translation table (TR.ar / TR.en): t.am / t.pm
// when present, else the direction decides, else English.
export var ampmOf = function (t) {
  if (t && t.am && t.pm) return [t.am, t.pm];
  if (t && t.dir === "rtl") return ["ص", "م"];
  return ["AM", "PM"];
};

// h (0-23), mi (0-59) → "h:mm AM/PM". The one place the 12-hour arithmetic lives.
// opts.withSeconds (owner 2026-10-27: the header clock only) → "h:mm:ss AM/PM";
// everything else stays h:mm.
export var clock12 = function (h, mi, t, opts, s) {
  h = Number(h); mi = Number(mi); s = Number(s) || 0;
  if (!(h >= 0 && h < 24) || !(mi >= 0 && mi < 60)) return "";
  var ap = ampmOf(t);
  var h12 = h % 12; if (h12 === 0) h12 = 12;
  var sec = opts && opts.withSeconds ? ":" + (s < 10 ? "0" + s : s) : "";
  return h12 + ":" + (mi < 10 ? "0" + mi : mi) + sec + " " + (h < 12 ? ap[0] : ap[1]);
};

// INSTANT → Cairo "h:mm AM/PM" (opts.withSeconds → "h:mm:ss AM/PM").
export var fmtTime12 = function (d, t, opts) {
  if (d == null || d === "") return "";
  var p = cairoClockParts(d);
  return p ? clock12(p.h, p.mi, t, opts, p.s) : "";
};

// INSTANT → Cairo "15 May 2026 · 2:32 PM" (the bell/session shape). `opts.year`
// false drops the year ("15 May · 2:32 PM"); `opts.numeric` true gives
// "15/05/2026 · 2:32 PM"; `opts.sep` replaces the " · " joiner.
export var fmtDateTime12 = function (d, t, opts) {
  if (d == null || d === "") return "";
  var p = cairoClockParts(d);
  if (!p) return "";
  var o = opts || {};
  var dd = p.d < 10 ? "0" + p.d : "" + p.d, mm = p.m < 10 ? "0" + p.m : "" + p.m;
  var date = o.numeric
    ? (dd + "/" + mm + (o.year === false ? "" : "/" + p.y))
    : (p.d + " " + MONTHS_SHORT[p.m - 1] + (o.year === false ? "" : " " + p.y));
  return date + (o.sep === undefined ? " · " : o.sep) + clock12(p.h, p.mi, t);
};

// NAIVE WALL-CLOCK STRING ("YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm", or bare
// "HH:mm") → "h:mm AM/PM". Digits only — no Date, no zone.
var WALL_RE = /(?:^|T| )(\d{1,2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;
export var fmtWallTime12 = function (s, t) {
  if (s == null) return "";
  var m = WALL_RE.exec(String(s).trim());
  return m ? clock12(m[1], m[2], t) : "";
};

// NAIVE WALL-CLOCK STRING → "dd/mm/yyyy h:mm AM/PM" (the side-panel Callback
// shape). The date digits come straight from the string too.
export var fmtWallDateTime12 = function (s, t) {
  if (s == null) return "";
  var str = String(s).trim();
  var dm = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  var clock = fmtWallTime12(str, t);
  if (!dm || !clock) return "";
  return dm[3] + "/" + dm[2] + "/" + dm[1] + " " + clock;
};

// ---- Excel (SheetJS, in the browser) -------------------------------------
// A REAL time cell, not text, so the column sorts. SheetJS serialises a Date by
// its LOCAL wall clock, so a Date built from the Cairo parts in the viewer's
// local constructor shows the Cairo clock in any viewer's zone.
export var EXCEL_TIME_FMT = "h:mm AM/PM";
export var EXCEL_DATETIME_FMT = "dd/mm/yyyy h:mm AM/PM";
export var excelCairoDate = function (d) {
  var p = cairoClockParts(d);
  return p ? new Date(p.y, p.m - 1, p.d, p.h, p.mi, 0, 0) : null;
};
// Set a 12-hour number format on every data cell of the named column (SheetJS
// json_to_sheet writes a Date as a numeric serial with a default "m/d/yy" z).
export var excelFormatColumn = function (XLSX, ws, header, fmt) {
  if (!XLSX || !ws || !ws["!ref"]) return;
  var r = XLSX.utils.decode_range(ws["!ref"]), col = -1;
  for (var c = r.s.c; c <= r.e.c; c++) {
    var hc = ws[XLSX.utils.encode_cell({ r: r.s.r, c: c })];
    if (hc && hc.v === header) { col = c; break; }
  }
  if (col < 0) return;
  for (var row = r.s.r + 1; row <= r.e.r; row++) {
    var cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
    if (cell && (cell.t === "d" || cell.t === "n")) cell.z = fmt;
  }
};
// A naive wall string IS the local wall clock already.
export var excelWallDate = function (s) {
  if (!s) return null;
  var m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(s).trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0) : null;
};
